// eslint-disable-next-line import/no-cycle
import assessmentSchemaService from '../../services/assessmentSchemaService';
// eslint-disable-next-line import/no-cycle
import assessmentService from '../../services/assessmentService';

export default {
  namespaced: true,

  state: {
    assessmentSchemas: [],
    currentAssessmentSchema: null,
    assessmentSchemaQuestions: [],
    currentQuestion: null,
    loading: false,
    error: null,
    schemaTemplates: null, // Current schema's notification templates
    // Last external evaluation dates per schema type
    // (e.g., { "mindset360": Date, "custom360": Date })
    lastExternalEvaluationDates: {},
  },

  mutations: {
    SET_ASSESSMENTS(state, assessmentSchemas) {
      state.assessmentSchemas = assessmentSchemas;
    },
    SET_CURRENT_ASSESSMENT(state, assessment) {
      state.currentAssessmentSchema = assessment;
    },
    CLEAR_CURRENT_ASSESSMENT(state) {
      state.currentAssessmentSchema = null;
    },
    SET_ASSESSMENT_QUESTIONS(state, questions) {
      state.assessmentQuestions = questions;
    },
    SET_CURRENT_QUESTION(state, question) {
      state.currentQuestion = question;
    },
    ADD_ASSESSMENT(state, assessment) {
      state.assessmentSchemas.push(assessment);
    },
    UPDATE_ASSESSMENT(state, updatedAssessment) {
      const index = state.assessmentSchemas.findIndex((a) => a._id === updatedAssessment._id);
      if (index !== -1) {
        state.assessmentSchemas.splice(index, 1, updatedAssessment);
      }
      state.currentAssessmentSchema = { ...updatedAssessment };
    },
    REMOVE_ASSESSMENT(state, assessmentId) {
      state.assessmentSchemas = state.assessmentSchemas.filter((a) => a._id !== assessmentId);
      if (state.currentAssessmentSchema && state.currentAssessmentSchema._id === assessmentId) {
        state.currentAssessmentSchema = null;
      }
    },
    ADD_QUESTION(state, question) {
      state.currentAssessmentSchema.assessmentQuestionArray.push(question);
    },
    UPDATE_QUESTION(state, updatedQuestion) {
      const index = state.currentAssessmentSchema.assessmentQuestionArray.findIndex(
        (q) => q._id === updatedQuestion._id,
      );
      if (index !== -1) {
        state.currentAssessmentSchema.assessmentQuestionArray.splice(index, 1, updatedQuestion);
      }
      if (state.currentQuestion && state.currentQuestion._id === updatedQuestion._id) {
        state.currentQuestion = updatedQuestion;
      }
    },
    REMOVE_QUESTION(state, questionId) {
      // Remove from assessmentQuestions array (with null check)
      if (state.assessmentQuestions && Array.isArray(state.assessmentQuestions)) {
        state.assessmentQuestions = state.assessmentQuestions.filter((q) => q._id !== questionId);
      }

      // Remove from current assessment schema's question array
      if (
        state.currentAssessmentSchema &&
        state.currentAssessmentSchema.assessmentQuestionArray &&
        Array.isArray(state.currentAssessmentSchema.assessmentQuestionArray)
      ) {
        state.currentAssessmentSchema.assessmentQuestionArray =
          state.currentAssessmentSchema.assessmentQuestionArray.filter((q) => q._id !== questionId);
      }

      // Clear current question if it's the one being deleted
      if (state.currentQuestion && state.currentQuestion._id === questionId) {
        state.currentQuestion = null;
      }
    },
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    UPDATE_SCHEMA_DIMENSION(state, dimension) {
      const index = state.currentAssessmentSchema.dimensions.findIndex(
        (d) => d.value === dimension.value,
      );
      if (index !== -1) {
        state.currentAssessmentSchema.dimensions.splice(index, 1, dimension);
      } else {
        state.currentAssessmentSchema.dimensions.push(dimension);
      }
    },
    REMOVE_SCHEMA_DIMENSION(state, dimensionValue) {
      state.currentAssessmentSchema.dimensions = state.currentAssessmentSchema.dimensions.filter(
        (d) => d.value !== dimensionValue,
      );
    },
    UPDATE_SCHEMA_DIMENSIONS(state, dimensions) {
      state.currentAssessmentSchema.dimensions = dimensions;
    },
    SET_SCHEMA_TEMPLATES(state, templates) {
      state.schemaTemplates = templates;
    },
    SET_LAST_EXTERNAL_EVALUATION_DATES(state, dates) {
      state.lastExternalEvaluationDates = dates;
    },
  },

  actions: {
    async fetchAssessments({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.getAllAssessmentSchemas();
        commit('SET_ASSESSMENTS', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch assessmentSchemas');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessmentSchemas',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchLastExternalEvaluationDates({ commit }) {
      try {
        const response = await assessmentService.getLastExternalEvaluationDates();
        commit('SET_LAST_EXTERNAL_EVALUATION_DATES', response.data || {});
      } catch (error) {
        // Silently fail - fallback to empty object (no cooldown restrictions)
        console.error('Failed to fetch last external evaluation dates:', error);
        commit('SET_LAST_EXTERNAL_EVALUATION_DATES', {});
      }
    },

    async fetchAssessment({ commit, dispatch }, assessmentId) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.getAssessmentSchema(assessmentId);
        commit('SET_CURRENT_ASSESSMENT', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessment',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createAssessment({ commit, dispatch }, assessmentData) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.createAssessmentSchema(assessmentData);
        commit('ADD_ASSESSMENT', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Assessment created successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to create assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to create assessment',
            severity: 'error',
          },
          { root: true },
        );
        return null;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateAssessment({ commit, dispatch }, { id, payload }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.updateAssessmentSchema(id, payload);
        commit('UPDATE_ASSESSMENT', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Assessment updated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update assessment',
            severity: 'error',
          },
          { root: true },
        );
        return null;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateExternalEvaluationConfig(
      { commit, dispatch },
      {
        schemaId,
        canCreateExternalEvaluation,
        externalEvaluationSchemaId,
      },
    ) {
      commit('SET_LOADING', true);
      try {
        // Send nested config so backend and getters stay in sync
        const payload = {
          config: {
            externalEvaluation: {
              canCreate: canCreateExternalEvaluation,
              schemaId: externalEvaluationSchemaId ?? undefined,
            },
          },
        };
        const response = await assessmentSchemaService.updateAssessmentSchema(
          schemaId,
          payload,
        );

        const updatedSchema = response.data;
        if (updatedSchema) {
          commit('UPDATE_ASSESSMENT', updatedSchema);
        }

        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update external evaluation schema');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update external evaluation schema',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateChildSchema(
      { commit, dispatch },
      { externalEvaluationSchemaId, isExternalEvaluationSchema, dimensions },
    ) {
      commit('SET_LOADING', true);
      try {
        await assessmentSchemaService.updateAssessmentSchema(
          externalEvaluationSchemaId,
          { dimensions, isExternalEvaluationSchema },
        );
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update child schema');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update child schema',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateReflexionGuidelineConfig({ commit, dispatch }, { id, config }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.updateReflexionGuidelineConfig(id, config);
        commit('UPDATE_ASSESSMENT', response.data.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Reflection guideline configuration updated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update reflection guideline configuration');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update reflection guideline configuration',
            severity: 'error',
          },
          { root: true },
        );
        return null;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteAssessment({ commit, dispatch }, assessmentId) {
      commit('SET_LOADING', true);
      try {
        await assessmentSchemaService.deleteAssessmentSchema(assessmentId);
        commit('REMOVE_ASSESSMENT', assessmentId);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Assessment deleted successfully',
            severity: 'success',
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to delete assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to delete assessment',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Question-related actions

    async fetchQuestions({ commit, dispatch }, assessmentId) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.getQuestions(assessmentId);
        commit('SET_ASSESSMENT_QUESTIONS', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch questions');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch questions',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchQuestion({ commit, dispatch }, { assessmentId, questionId }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.getQuestion(assessmentId, questionId);
        commit('SET_CURRENT_QUESTION', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch question');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch question',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async addQuestion({ commit, dispatch }, { assessmentId, questionData }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.addQuestion(assessmentId, questionData);
        commit('ADD_QUESTION', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Question added successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to add question');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to add question',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async addQuestions({ commit, dispatch }, { assessmentId, questionsData }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.addQuestions(assessmentId, questionsData);
        response.data.forEach((question) => {
          commit('ADD_QUESTION', question);
        });
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Questions added successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        console.log('======================> ', error);
        commit('SET_ERROR', error.response?.data?.message || 'Failed to add questions');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to add questions',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateQuestion({ commit, dispatch }, { assessmentId, questionId, questionData }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.updateQuestion(
          assessmentId,
          questionId,
          questionData,
        );
        commit('UPDATE_QUESTION', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Question updated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update question');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update question',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateQuestions({ commit, dispatch }, { assessmentId, questionsData }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.updateQuestions(assessmentId, questionsData);
        response.data.forEach((question) => {
          commit('UPDATE_QUESTION', question);
        });
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Questions updated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update questions');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update questions',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteQuestion({ commit, dispatch }, { assessmentId, questionId }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.deleteQuestion(assessmentId, questionId);

        // Handle 204 No Content response
        if (response.status === 204 || response.status === 200) {
          commit('REMOVE_QUESTION', questionId);
        }
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Question deleted successfully',
            severity: 'success',
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to delete question');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to delete question',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteQuestions({ commit, dispatch }, { assessmentId, questionIds }) {
      commit('SET_LOADING', true);
      try {
        await assessmentSchemaService.deleteQuestions(assessmentId, questionIds);
        questionIds.forEach((id) => {
          commit('REMOVE_QUESTION', id);
        });
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Questions deleted successfully',
            severity: 'success',
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to delete questions');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to delete questions',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateSchemaDimension({ commit, dispatch }, { schemaId, dimension }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.updateSchemaDimension(schemaId, dimension);
        commit('UPDATE_SCHEMA_DIMENSION', response.data);
        commit('SET_ERROR', null);
        dispatch('notifications/showToast', {
          detail: 'Schema dimension updated successfully',
          severity: 'success',
        });
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update schema dimension');
        dispatch('notifications/showToast', {
          detail: 'Failed to update schema dimension',
          severity: 'error',
        });
      }
    },

    async removeSchemaDimension({ commit, dispatch }, { schemaId, dimensionValue }) {
      commit('SET_LOADING', true);
      try {
        await assessmentSchemaService.removeSchemaDimension(schemaId, dimensionValue);
        commit('REMOVE_SCHEMA_DIMENSION', dimensionValue);
        commit('SET_ERROR', null);
        dispatch('notifications/showToast', {
          detail: 'Schema dimension removed successfully',
          severity: 'success',
        });
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to remove schema dimension');
        dispatch('notifications/showToast', {
          detail: 'Failed to remove schema dimension',
          severity: 'error',
        });
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateSchemaDimensions({ commit, dispatch }, { schemaId, dimensions }) {
      commit('SET_LOADING', true);
      try {
        const dimensionIds = dimensions.map((dimension) => dimension._id || dimension);
        const response = await assessmentSchemaService.updateSchemaDimensions(
          schemaId,
          dimensionIds,
        );
        commit('UPDATE_SCHEMA_DIMENSIONS', response.data);
        commit('SET_ERROR', null);
        dispatch('notifications/showToast', {
          detail: 'Schema dimensions updated successfully',
          severity: 'success',
        });
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update schema dimensions');
        dispatch('notifications/showToast', {
          detail: 'Failed to update schema dimensions',
          severity: 'error',
        });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async sendDevTipsForSchema({ commit, dispatch }, schemaId) {
      try {
        commit('SET_LOADING', true);
        const response = await assessmentSchemaService.sendDevTipsForSchema(schemaId);

        // Show success message with details from the backend
        dispatch(
          'notifications/showToast',
          {
            detail: response.data.message,
            severity: 'success',
          },
          { root: true },
        );

        return response;
      } catch (error) {
        console.error('Error sending DevTips for schema:', error);
        commit('SET_ERROR', error.message);

        // Handle different types of errors
        let errorMessage = 'Failed to send development tips. Please try again.';

        if (error.response) {
          const { status } = error.response;
          const message = error.response.data?.message;

          switch (status) {
            case 404:
              errorMessage = 'Assessment schema not found.';
              break;
            case 400:
              errorMessage = message || 'DevTips are not enabled for this assessment.';
              break;
            case 401:
              errorMessage = 'You are not authorized to perform this action.';
              break;
            case 403:
              errorMessage = 'You do not have permission to send DevTips.';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = message || errorMessage;
          }
        } else if (error.request) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }

        dispatch(
          'notifications/showToast',
          {
            detail: errorMessage,
            severity: 'error',
          },
          { root: true },
        );

        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async triggerReflectionGuidelinesForSchema({ commit, dispatch }, schemaId) {
      try {
        commit('SET_LOADING', true);
        const response = await assessmentSchemaService
          .triggerReflectionGuidelinesForSchema(schemaId);

        // Show success message with details from the backend
        dispatch(
          'notifications/showToast',
          {
            detail: response.data.message,
            severity: 'success',
          },
          { root: true },
        );

        return response;
      } catch (error) {
        console.error('Error triggering reflection guidelines for schema:', error);
        commit('SET_ERROR', error.message);

        // Handle different types of errors
        let errorMessage = 'Failed to trigger reflection guidelines. Please try again.';

        if (error.response) {
          const { status } = error.response;
          const message = error.response.data?.message;

          switch (status) {
            case 404:
              errorMessage = 'Assessment schema not found.';
              break;
            case 400:
              errorMessage = message || 'Reflection guidelines are not enabled for this assessment.';
              break;
            case 401:
              errorMessage = 'You are not authorized to perform this action.';
              break;
            case 403:
              errorMessage = 'You do not have permission to trigger reflection guidelines.';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = message || errorMessage;
          }
        } else if (error.request) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }

        dispatch(
          'notifications/showToast',
          {
            detail: errorMessage,
            severity: 'error',
          },
          { root: true },
        );

        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Create assessment from schema (handles both normal and rotated schemas)
     * @param {Object} context - Vuex context
     * @param {Object} payload - { schemaId, userId?, options? }
     */
    async createAssessmentFromSchema(
      { commit, dispatch },
      { schemaId, userId, options = {} },
    ) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.createAssessmentFromSchema(
          schemaId,
          userId, // Can be undefined to create for all users
          options,
        );

        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Assessment created with rotated questions successfully',
            severity: 'success',
          },
          { root: true },
        );

        return response.data;
      } catch (error) {
        const data = error.response?.data;
        const isRoleDenied = data?.error === 'ASSESSMENT_ASSIGNMENT_ROLE_DENIED';
        const detailMsg =
          typeof data?.message === 'string'
            ? data.message
            : 'Failed to create assessment with rotated questions';
        commit('SET_ERROR', detailMsg);
        if (isRoleDenied) {
          dispatch('assessmentRoleDenied/maybeShowFromApiError', error, { root: true });
        } else {
          dispatch(
            'notifications/showToast',
            {
              detail: detailMsg,
              severity: 'error',
            },
            { root: true },
          );
        }
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Generate test data for development purposes (DEV ONLY)
     * @param {Object} context - Vuex context
     * @param {Object} payload - { schemaId }
     */
    async generateTestData({ commit, dispatch }, { schemaId }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.generateTestData(schemaId);

        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: `Test data generated successfully: ${response.data.message}`,
            severity: 'success',
          },
          { root: true },
        );

        return response.data;
      } catch (error) {
        commit(
          'SET_ERROR',
          error.response?.data?.message || 'Failed to generate test data',
        );
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to generate test data',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Generate random assessment data for selected users based on schema chart type
     * @param {Object} context - Vuex context
     * @param {Object} payload - { schemaId, userIds }
     */
    async generateRandomData({ commit, dispatch }, { schemaId, userIds }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.generateRandomData(schemaId, userIds);
        commit('SET_ERROR', null);
        return response.data;
      } catch (error) {
        commit(
          'SET_ERROR',
          error.response?.data?.message || 'Failed to generate random data',
        );
        dispatch(
          'notifications/showToast',
          {
            detail: error.response?.data?.message || 'Failed to generate random data',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getReflexionGuidelineConfig({ commit, dispatch }, schemaId) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.getReflexionGuidelineConfig(schemaId);

        if (response.data.success) {
          commit('SET_ERROR', null);
          return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to get reflection guideline config');
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to get reflection guideline config');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to load reflection guideline configuration',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getSchemasWithReflexionGuidelines({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentSchemaService.getSchemasWithReflexionGuidelines();

        if (response.data.success) {
          commit('SET_ERROR', null);
          return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to get schemas with reflection guidelines');
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to get schemas with reflection guidelines');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to load schemas with reflection guidelines',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async sendDevTipToSpecificUsers({ commit, dispatch }, { userIds, schemaId }) {
      commit('SET_LOADING', true);
      try {
        // Use dynamic import to avoid circular dependency
        const { default: devTipsService } = await import('@/services/devTipsService');
        const response = await devTipsService.sendDevTipToUsers(userIds, schemaId);

        if (response.success) {
          commit('SET_ERROR', null);
          dispatch(
            'notifications/showToast',
            {
              detail: `DevTips sent successfully! ${response.data.tipsRecommended} tips sent to ${response.data.usersProcessed} users.`,
              severity: 'success',
            },
            { root: true },
          );
          return response;
        }
        throw new Error(response.message || 'Failed to send DevTips');
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to send DevTips');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to send DevTips',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchSchemaTemplates({ commit, dispatch }, schemaId) {
      try {
        const response = await assessmentSchemaService.getSchemaTemplates(schemaId);
        const templates = response.data.data || response.data || null;
        commit('SET_SCHEMA_TEMPLATES', templates);
        return templates;
      } catch (error) {
        console.error('Failed to fetch schema templates:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch schema templates');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to load notification templates',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    async updateSchemaTemplates({ commit, dispatch }, { schemaId, templates }) {
      try {
        commit('SET_LOADING', true);
        await assessmentSchemaService.updateSchemaTemplates(schemaId, templates);
        // Reload templates after update
        await dispatch('fetchSchemaTemplates', schemaId);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Notification templates updated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return true;
      } catch (error) {
        console.error('Failed to update schema templates:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update schema templates');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update notification templates',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

  },

  getters: {
    // eslint-disable-next-line arrow-body-style
    getAssessmentSchemaById: (state) => (id) => {
      return state.assessmentSchemas.find((assessment) => assessment._id === id);
    },
    // eslint-disable-next-line arrow-body-style
    getQuestionById: (state) => (id) => {
      return state.assessmentQuestions.find((question) => question._id === id);
    },
    currentAssessmentSchema: (state) => state.currentAssessmentSchema,
    assessmentSchemas: (state) => state.assessmentSchemas,
    isLoading: (state) => state.loading,
    error: (state) => state.error,

    // Validation getters
    validateQuestionDimension: (state) => (question) => {
      const assignedDimensions = state.currentAssessmentSchema?.dimensions || [];
      if (!Array.isArray(assignedDimensions)) return false;
      return assignedDimensions.some((dim) => dim.value === question.properties?.dimensionName);
    },

    getQuestionsForDimension: (state) => (dimensionValue) => state
      .currentAssessmentSchema?.assessmentQuestionArray?.filter(
        (q) => q.properties?.dimensionName === dimensionValue,
      ) || [],

    hasDimensionQuestions: (state, getters) => (dimensionValue) => getters
      .getQuestionsForDimension(dimensionValue).length > 0,

    // Validation summary getters
    hasInvalidQuestions: (state, getters) => {
      if (!state.currentAssessmentSchema?.assessmentQuestionArray) return false;
      return state.currentAssessmentSchema.assessmentQuestionArray.some(
        (question) => !getters.validateQuestionDimension(question),
      );
    },

    hasUnassignedDimensions: (state, getters) => {
      const assignedDimensions = state.currentAssessmentSchema?.dimensions || [];
      if (!Array.isArray(assignedDimensions)) return false;
      return assignedDimensions.some(
        (dimension) => !getters.hasDimensionQuestions(dimension.value),
      );
    },

    getInvalidQuestions: (state, getters) => {
      if (!state.currentAssessmentSchema?.assessmentQuestionArray) return [];
      return state.currentAssessmentSchema.assessmentQuestionArray.filter(
        (question) => !getters.validateQuestionDimension(question),
      );
    },

    getUnassignedDimensions: (state, getters) => {
      const assignedDimensions = state.currentAssessmentSchema?.dimensions || [];
      if (!Array.isArray(assignedDimensions)) return [];
      return assignedDimensions.filter(
        (dimension) => !getters.hasDimensionQuestions(dimension.value),
      );
    },
    schemaTemplates: (state) => state.schemaTemplates,

    // Get all schemas marked as external evaluation schemas
    getExternalEvaluationSchemas: (state) => state.assessmentSchemas
      .filter((schema) => schema.isExternalEvaluationSchema === true),

    /**
     * Get the external evaluation schema linked to a given assessment type
     * Uses config.externalEvaluation.schemaId or externalEvaluationSchemaId
     * @param {string} type - The assessment type (e.g., 'oim')
     * @returns {Object|null} The linked external evaluation schema or null
     */
    getExternalEvaluationSchemaForType: (state, getters) => (type) => {
      const parentSchema = state.assessmentSchemas.find((schema) => schema.type === type);
      if (!parentSchema) return null;
      const schemaId = getters.getExternalEvaluationSchemaId(parentSchema);
      if (!schemaId) return null;
      const idStr = typeof schemaId === 'string' ? schemaId : (schemaId?.toString?.() ?? schemaId);
      return state.assessmentSchemas.find(
        (schema) => (schema._id?.toString?.() ?? schema._id) === idStr,
      ) || null;
    },

    /**
     * Get the parent schema that links to a given external evaluation schema
     * @param {string} schemaId - The external evaluation schema ID
     * @returns {Object|null} The parent schema or null
     */
    getParentSchemaForExternalEvaluation: (state, getters) => (schemaId) => {
      const idStr = typeof schemaId === 'string' ? schemaId : (schemaId?.toString?.() ?? schemaId);
      return state.assessmentSchemas.find((schema) => {
        const linkedId = getters.getExternalEvaluationSchemaId(schema);
        if (linkedId == null) return false;
        const linkedStr = typeof linkedId === 'string' ? linkedId : (linkedId?.toString?.() ?? linkedId);
        return linkedStr === idStr;
      }) || null;
    },

    /**
     * Check if an assessment type is an external evaluation type
     * @param {string} type - The assessment type
     * @returns {boolean} True if the type is an external evaluation schema
     */
    isExternalEvaluationType: (state) => (type) => {
      const schema = state.assessmentSchemas.find((s) => s.type === type);
      return schema?.isExternalEvaluationSchema === true;
    },

    /**
     * Get the parent assessment type that can be compared with a given external evaluation type
     * @param {string} externalEvalType - The external evaluation type
     * @returns {string|null} The parent assessment type or null
     */
    getParentTypeForExternalEvaluation: (state, getters) => (externalEvalType) => {
      const externalSchema = state.assessmentSchemas.find((s) => s.type === externalEvalType);
      if (!externalSchema) return null;

      const parentSchema = getters.getParentSchemaForExternalEvaluation(externalSchema._id);
      return parentSchema?.type || null;
    },

    /**
     * Get the external evaluation type linked to a parent assessment type
     * @param {string} parentType - The parent assessment type
     * @returns {string|null} The external evaluation type or null
     */
    getExternalEvaluationTypeForParent: (state, getters) => (parentType) => {
      const externalSchema = getters.getExternalEvaluationSchemaForType(parentType);
      return externalSchema?.type || null;
    },

    /**
     * Check if an assessment type can create external evaluations
     * @param {string} type - The assessment type
     * @returns {boolean} True if external evaluations can be created
     */
    canCreateExternalEvaluationForType: (state, getters) => (type) => {
      const schema = state.assessmentSchemas.find((s) => s.type === type);
      if (!schema) return false;
      return getters.getCanCreateExternalEvaluation(schema) === true
        && getters.getExternalEvaluationSchemaId(schema) != null;
    },

    /**
     * Get the last external evaluation dates per schema type
     * @returns {Object} Object mapping schema type to last evaluation date
     */
    getLastExternalEvaluationDates: (state) => state.lastExternalEvaluationDates,

    // ============================================
    // Config-only helpers: read ONLY from config.* (no root-level fallback)
    // ============================================

    /**
     * Get DevTips enabled status from schema (config only)
     */
    getDevTipsEnabled: () => (schema) => schema?.config?.devTips?.enabled ?? false,

    /**
     * Get DevTips period from schema (config only)
     */
    getDevTipsPeriod: () => (schema) => schema?.config?.devTips?.schedule?.period ?? 'weekly',

    /**
     * Get DevTips schedule hour from schema
     * Checks config.devTips.schedule.hour first, defaults to 8
     */
    getDevTipsHour: () => (schema) => {
      if (schema?.config?.devTips?.schedule?.hour !== undefined) {
        return schema.config.devTips.schedule.hour;
      }
      return 8; // Default 8 AM
    },

    /**
     * Get DevTips schedule dayOfWeek from schema
     * Checks config.devTips.schedule.dayOfWeek first, defaults to 1 (Monday)
     */
    getDevTipsDayOfWeek: () => (schema) => {
      if (schema?.config?.devTips?.schedule?.dayOfWeek !== undefined) {
        return schema.config.devTips.schedule.dayOfWeek;
      }
      return 1; // Default Monday
    },

    /**
     * Get DevTips schedule dayOfMonth from schema
     * Checks config.devTips.schedule.dayOfMonth first, defaults to 1
     */
    getDevTipsDayOfMonth: () => (schema) => {
      if (schema?.config?.devTips?.schedule?.dayOfMonth !== undefined) {
        return schema.config.devTips.schedule.dayOfMonth;
      }
      return 1; // Default 1st of month
    },

    /**
     * Get Question Rotation enabled status from schema (config only)
     */
    getQuestionRotationEnabled: () => (schema) => (
      schema?.config?.questionRotation?.enabled ?? false
    ),

    /**
     * Get Question Rotation period from schema (config only)
     */
    getQuestionRotationPeriod: () => (schema) => schema?.config?.questionRotation?.schedule?.period ?? 'weekly',

    /**
     * Get Question Rotation schedule hour from schema
     * Checks config.questionRotation.schedule.hour first, defaults to 8
     */
    getQuestionRotationHour: () => (schema) => {
      if (schema?.config?.questionRotation?.schedule?.hour !== undefined) {
        return schema.config.questionRotation.schedule.hour;
      }
      return 8; // Default 8 AM
    },

    /**
     * Get Question Rotation schedule dayOfWeek from schema
     * Checks config.questionRotation.schedule.dayOfWeek first, defaults to 1 (Monday)
     */
    getQuestionRotationDayOfWeek: () => (schema) => {
      if (schema?.config?.questionRotation?.schedule?.dayOfWeek !== undefined) {
        return schema.config.questionRotation.schedule.dayOfWeek;
      }
      return 1; // Default Monday
    },

    /**
     * Get Question Rotation schedule dayOfMonth from schema
     * Checks config.questionRotation.schedule.dayOfMonth first, defaults to 1
     */
    getQuestionRotationDayOfMonth: () => (schema) => {
      if (schema?.config?.questionRotation?.schedule?.dayOfMonth !== undefined) {
        return schema.config.questionRotation.schedule.dayOfMonth;
      }
      return 1; // Default 1st of month
    },

    /**
     * Get Question Source from schema (config only)
     */
    getQuestionSource: () => (schema) => schema?.config?.questionRotation?.questionSource ?? 'direct',

    /**
     * Get Total Questions from schema (config only)
     */
    getTotalQuestions: () => (schema) => schema?.config?.questionRotation?.totalQuestions ?? 6,

    getStickyQuestionsEnabled: () => (schema) => schema?.config?.questionRotation?.stickyQuestionsEnabled ?? false,

    getStickyQuestionsCount: () => (schema) => schema?.config?.questionRotation?.stickyQuestionsCount ?? 0,

    getStickyQuestionSource: () => (schema) => schema?.config?.questionRotation?.stickyQuestionSource ?? 'direct',

    /**
     * Get Level System enabled status from schema (config only)
     */
    getLevelSystemEnabled: () => (schema) => schema?.config?.levelSystem?.enabled ?? false,

    /**
     * Get XP per completion from schema (config only)
     */
    getXpPerCompletion: () => (schema) => schema?.config?.levelSystem?.xpPerCompletion ?? 0,

    /**
     * Get First Login Task enabled status from schema (config only)
     */
    getFirstLoginTaskEnabled: () => (schema) => schema?.config?.firstLoginTask?.enabled ?? false,

    /**
     * Get External Evaluation canCreate from schema (config only)
     */
    getCanCreateExternalEvaluation: () => (schema) => (
      schema?.config?.externalEvaluation?.canCreate ?? false
    ),

    /**
     * Get External Evaluation schema ID from schema (config only)
     */
    getExternalEvaluationSchemaId: () => (schema) => (
      schema?.config?.externalEvaluation?.schemaId ?? null
    ),

    /**
     * Get Admin Creation canBeCreatedByAdmin from schema (config only)
     */
    getCanBeCreatedByAdmin: () => (schema) => (
      schema?.config?.adminCreation?.canBeCreatedByAdmin ?? false
    ),

    /**
     * Get View Ref Guideline Results from schema (config only)
     */
    getViewRefGuidelineResults: () => (schema) => (
      schema?.config?.reflexionGuideline?.viewResults ?? false
    ),

  },
};
